<script setup lang="ts">
import { useHead } from '@unhead/vue'
import { Crisp } from 'crisp-sdk-web'
import { computed, onMounted, onUnmounted, ref, toRef, watch } from 'vue'
import { useUserStore } from './stores/user'
import { useConnectionsStore } from './stores/connections'
import ConnectionOnboardingDialog from './components/dialogs/ConnectionOnboardingDialog.vue'
import { useReturnRulesLatest } from '~/stores/returnRulesLatest'
import { useNotificationsForLoggedInUser } from '~/compositions/configureNotifications'
import { onOrgChangedInOtherTab } from '~/managers/orgChangeManagement'
import { waitForExtensionDetection } from '~/utils/extensionDetection'
import SuggestExtensionInstallationPopup from '~/components/SuggestExtensionInstallationPopup.vue'
import { useExtensionPopupStore } from '~/stores/extensionPopup'
import { useAppModalPriority } from '~/compositions/useAppModalPriority'

useHead({
  title: 'Dray Dog',
})

const userStore = useUserStore()
const connectionsStore = useConnectionsStore()
const extensionPopupStore = useExtensionPopupStore()
const { extensionPopupIsAllowed } = useAppModalPriority()
useNotificationsForLoggedInUser()

const userIsLoggedIn = toRef(userStore, 'loggedIn')
const CRISP_WEBSITE_ID = 'b24d4b45-754d-4848-91a1-948b441a4da6'
Crisp.configure(CRISP_WEBSITE_ID, {})

function isBrowserCompatible() {
  const ua = window.navigator.userAgent
  // Only allow Chrome
  return /Chrome\//.test(ua)
}

const extensionInstalled = ref<boolean | null>(null)

onMounted(async () => {
  if (isBrowserCompatible()) {
    extensionInstalled.value = await waitForExtensionDetection()
  } else {
    extensionInstalled.value = false // Not compatible, so not installed
  }
})

const showInstallBrowserExtensionPopup = computed(() => {
  return (
    userIsLoggedIn.value &&
    extensionInstalled.value === false &&
    isBrowserCompatible() &&
    extensionPopupStore.canPromptInstallation &&
    extensionPopupIsAllowed.value
  )
})

watch(
  userIsLoggedIn,
  async (isLoggedIn) => {
    if (isLoggedIn) {
      useReturnRulesLatest() // Always loaded and connected
      Crisp.chat.show()
    } else {
      // Reset connections cache on logout so the next login always fetches fresh data.
      // Without this, loadConnectionsIfNotLoaded() skips the fetch when a new user
      // logs in, potentially showing stale data from the previous session.
      connectionsStore.reset()
      Crisp.chat.hide()
    }
  },
  { immediate: true }
)

// Separate from the watcher above and gated on `sessionReady` rather than
// `loggedIn`: `loggedIn` flips true before `userInfo` (and thus `isSubCarrier`
// etc.) hydrates, so checking those role flags at the same instant as
// `loggedIn` would read their default `false` and fetch connections for a
// role that must not have them.
watch(
  () => userStore.sessionReady,
  (ready) => {
    if (!ready || !userIsLoggedIn.value) return
    if (!userStore.isTerminalOrg && !userStore.isSubCarrier) {
      connectionsStore.loadConnectionsIfNotLoaded()
    }
  },
  { immediate: true }
)

const authChannel = onOrgChangedInOtherTab((newOrgID) => {
  // If another tab switches to an org that is different than this tab, we reload the
  // page to pickup the new org from localStorage
  if (userStore.currentOrg?.id && newOrgID !== userStore.currentOrg?.id) {
    const url = new URL(window.location.href)
    url.searchParams.delete('scac')
    url.searchParams.delete('org_id')
    // Directly navigating here so that our router beforeResolve handlers doesn't add
    // the scac or org_id parameters to the URL which would then trigger another page
    // reload to switch back to the original org and would make an infinite loop.
    window.location.href = url.toString()
  }
})
onUnmounted(() => {
  authChannel.close()
})
</script>

<template>
  <router-view />
  <SuggestExtensionInstallationPopup
    v-if="showInstallBrowserExtensionPopup"
    :visible="true"
    @close="extensionInstalled = true"
  />
  <ConnectionOnboardingDialog
    v-if="!showInstallBrowserExtensionPopup && userIsLoggedIn"
  />
</template>
